import React, { useRef, useContext } from 'react';
import {
  DetailContext,
  SnackbarContext,
  AbortableFetch,
  UserContext,
  useEventCallback,
  DetailHandle,
  abortableFetch,
  PromptContext,
  usePrompts,
} from '@eas/common-web';
import { Me, BoilerManufacturer, DictionaryBoilerModel } from '../../../models';
import { Messages, EvidenceAPI, Permission } from '../../../enums';
import { unstable_batchedUpdates } from 'react-dom';

/**
 * Api call
 *
 * @param model
 */
export function callApi(model: DictionaryBoilerModel) {
  return abortableFetch(
    `${EvidenceAPI.DICTIONARY_BOILER_MODELS}/${model.id}/active`,
    {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    }
  );
}

/**
 * Zneplatnění modelu
 */
const PROMPT_KEY = 'DEACTIVATE_MODEL';

export function useDeactivateModelDialog({
  boilerModels,
  selectedIndex,
  setSelectedIndex,
}: {
  boilerModels: DictionaryBoilerModel[];
  selectedIndex: number;
  setSelectedIndex: (index: number | undefined) => void;
}) {
  /**
   * Context stuff.
   */
  const { onPersisted, source } = useContext<DetailHandle<BoilerManufacturer>>(
    DetailContext
  );
  const { showSnackbar } = useContext(SnackbarContext);
  const { testPrompt } = useContext(PromptContext);
  const { hasPermission, reload } = useContext<UserContext<Me>>(UserContext);

  /**
   * Fetch ref.
   */
  const fetch = useRef<AbortableFetch | null>(null);

  /**
   * Dialog.
   */
  usePrompts(
    [
      {
        key: PROMPT_KEY,
        dialogTitle: 'Zneplatnění modelu',
        dialogText: (
          <>
            Skutečně chcete zneplatnit model{' '}
            <b>
              {selectedIndex !== undefined
                ? boilerModels[selectedIndex]?.name
                : ''}
            </b>
            ?
          </>
        ),
        dialogWidth: 600,
      },
    ],
    [selectedIndex, boilerModels]
  );

  const handleDeactivateModel = useEventCallback(async () => {
    testPrompt({
      key: PROMPT_KEY,
      submitCallback: async () => {
        try {
          source.setLoading(true);

          if (fetch.current !== null) {
            fetch.current.abort();
          }

          fetch.current = callApi(boilerModels[selectedIndex]);

          await fetch.current.raw();

          unstable_batchedUpdates(() => {
            showSnackbar(...Messages.Ozo.DEACTIVATE_MODEL.SUCCESS);
            source.setLoading(false);
          });

          setSelectedIndex(undefined);
          onPersisted(source.data!.id);
          await source.refresh();
          await reload();
        } catch (err) {
          source.setLoading(false);

          if (err.name !== 'AbortError') {
            showSnackbar(...Messages.Ozo.DEACTIVATE_MODEL.ERROR);
            throw err;
          }
          return undefined;
        }
      },
    });
  });

  const canDeactivateModel =
    selectedIndex !== undefined &&
    boilerModels?.[selectedIndex]?.active === true &&
    hasPermission(Permission.Ozo.OZO_MODEL_UPDATE, {
      searchInSubjectPerms: true,
    });

  return {
    handleDeactivateModel,
    canDeactivateModel,
  };
}
